import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import type { TabsProps } from 'antd';
import { ThemeProvider } from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import {
  DEFAULT_TAB_SLUG,
  PAGE_SUBTITLE,
  PAGE_TITLE,
  TAB_CONFIG,
  TAB_DESCRIPTIONS,
  isKnownTabSlug,
  tabRoute,
} from './constants';
import {
  HeaderLeft,
  HeaderSection,
  PageSubtitle,
  PageTitle,
  PageWrapper,
  StyledTabs,
  TabDescription,
} from './Styles';
import CreatorsTab from './CreatorsTab';
import TermsTab from './TermsTab';
import LeaderboardTab from './LeaderboardTab';
import AttributionsTab from './AttributionsTab';
import PayoutsTab from './PayoutsTab';

/**
 * Tab slug -> component. Kept here rather than in `constants.ts` so that module
 * stays free of component imports (and free of import cycles).
 */
const TAB_COMPONENTS: Record<string, React.ComponentType> = {
  creators: CreatorsTab,
  terms: TermsTab,
  leaderboard: LeaderboardTab,
  attributions: AttributionsTab,
  payouts: PayoutsTab,
};

/**
 * Creator Program shell.
 *
 * Follows the Menu feature's tab pattern (`components/Menu/Menu.js`): the active tab
 * lives in the URL (`/creator-program/:tab`) rather than in `useState`, so deep links,
 * refreshes and browser back/forward all work. Unknown slugs redirect to the default
 * instead of silently falling back to the first tab.
 */
const CreatorProgram: React.FC = () => {
  const { tab: tabSlug } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const activeSlug = isKnownTabSlug(tabSlug) ? (tabSlug as string) : DEFAULT_TAB_SLUG;

  useEffect(() => {
    if (tabSlug && !isKnownTabSlug(tabSlug)) {
      navigate(tabRoute(DEFAULT_TAB_SLUG), { replace: true });
    }
  }, [tabSlug, navigate]);

  const themeConfig = useMemo(
    () => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }),
    [isDarkMode]
  );
  const antdConfig = useMemo(() => getAntdThemeConfig(isDarkMode), [isDarkMode]);

  const items = useMemo<TabsProps['items']>(
    () =>
      TAB_CONFIG.map((tab) => {
        const Component = TAB_COMPONENTS[tab.slug];
        return {
          key: tab.slug,
          label: tab.label,
          children: (
            <>
              <TabDescription>{TAB_DESCRIPTIONS[tab.slug]}</TabDescription>
              <Component />
            </>
          ),
        };
      }),
    []
  );

  return (
    <ThemeProvider theme={themeConfig}>
      <ConfigProvider theme={antdConfig}>
        <PageWrapper>
          <HeaderSection>
            <HeaderLeft>
              <PageTitle>{PAGE_TITLE}</PageTitle>
              <PageSubtitle>{PAGE_SUBTITLE}</PageSubtitle>
            </HeaderLeft>
          </HeaderSection>

          <StyledTabs
            activeKey={activeSlug}
            onChange={(key: string) => navigate(tabRoute(key))}
            items={items}
            destroyOnHidden
          />
        </PageWrapper>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default CreatorProgram;
